/**
 * Pending changes, bulk operations, and data clearing for sync.
 */

import { ensureDB, getDB, getAll, getAllForUserRaw, getById, remove, now } from './helpers';
import { deleteBookFile, deleteBookCover } from './bookStore';

// =====================================================
// Pending changes (for offline sync)
// =====================================================

/**
 * Return pending changes belonging to the given user.
 *
 * `pending_changes` has no user_id index (schema predates multi-account on
 * same device), so we filter by inspecting each row's `data.user_id`, which
 * is set by `addPendingChange` — it stores the full record as-is, so the
 * original user_id ride-alongs with the payload.
 *
 * When `userId` is omitted, legacy behaviour returns every row. All current
 * callers pass `userId`; the no-arg path is retained only to avoid breaking
 * any in-flight work that hasn't been updated yet.
 *
 * Cross-account residue (data.user_id !== userId) is NOT deleted here —
 * removal happens during clearCurrentUserData(staleUserId) in the login/
 * account-switch flow (see AuthContext.jsx switchToToken). Filtering here is
 * the sync-time safety net: it prevents a residual record from being pushed
 * to the server under the wrong account even if the clearing path was missed.
 * See plan twinkly-jingling-pretzel.md for the full defense-in-depth design.
 *
 * @param {string} [userId]
 */
export async function getPendingChanges(userId) {
    if (!(await ensureDB())) return [];
    const db = getDB();
    const all = await new Promise((resolve, reject) => {
        const tx = db.transaction('pending_changes', 'readonly');
        const store = tx.objectStore('pending_changes');
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
    if (!userId) return all;
    return all.filter((p) => {
        // Records whose payload has no user_id (legacy data or non-user-scoped
        // stores) pass through — they won't be rejected by the backend's L2.5
        // check either (that check only triggers when payload.user_id is set
        // AND doesn't match JWT sub).
        const payloadUserId = p?.data?.user_id;
        if (payloadUserId == null) return true;
        return payloadUserId === userId;
    });
}

/**
 * List every distinct user_id currently present in the books store. Used by
 * the login flow to detect IDB residue from a previous account even when
 * localStorage.token was cleared without a proper signOut (see
 * AuthContext.jsx switchToToken). We scan the books index specifically because
 * (a) books is the root of user-scoped data in Reader, and (b) cross-account
 * residue always manifests as a book record — highlights/notes/bookmarks are
 * FK-descendants and can't exist without their books.
 *
 * @returns {Promise<Set<string>>} set of user_id values found in books store
 */
export async function listDistinctUserIdsInBooks() {
    const ids = new Set();
    if (!(await ensureDB())) return ids;
    const db = getDB();
    if (!db.objectStoreNames.contains('books')) return ids;
    return new Promise((resolve, reject) => {
        const tx = db.transaction('books', 'readonly');
        const store = tx.objectStore('books');
        const index = store.index('user_id');
        // openKeyCursor walks index entries without loading full records —
        // O(records) but each step only reads the user_id key, not the row.
        const req = index.openKeyCursor();
        req.onsuccess = (event) => {
            const cursor = event.target.result;
            if (cursor) {
                if (typeof cursor.key === 'string' && cursor.key) ids.add(cursor.key);
                cursor.continue();
            } else {
                resolve(ids);
            }
        };
        req.onerror = () => reject(req.error);
    });
}

/** Clear all pending changes (used by logout). */
export async function clearPendingChanges() {
    if (!(await ensureDB())) return;
    const db = getDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction('pending_changes', 'readwrite');
        const store = tx.objectStore('pending_changes');
        const request = store.clear();

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
}

/**
 * Delete only the pending changes that were included in a sync batch.
 * If a record was overwritten during sync (newer updated_at), it is kept.
 * @param {Array<{id: string, updated_at: string}>} snapshot
 */
export async function clearPendingChangesBatch(snapshot) {
    if (!(await ensureDB()) || !snapshot.length) return;
    const db = getDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction('pending_changes', 'readwrite');
        const store = tx.objectStore('pending_changes');

        for (const entry of snapshot) {
            const getReq = store.get(entry.id);
            getReq.onsuccess = () => {
                const current = getReq.result;
                if (current && current.updated_at === entry.updated_at) {
                    store.delete(entry.id);
                }
            };
        }

        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

// =====================================================
// Bulk operations for sync
// =====================================================

export async function bulkPut(storeName, items) {
    if (!(await ensureDB())) return;
    const db = getDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);

        for (const item of items) {
            // Remove _pending flag when receiving from server
            const cleanItem = { ...item };
            delete cleanItem._pending;
            store.put(cleanItem);
        }

        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

// =====================================================
// Data clearing
// =====================================================

/**
 * Wipe ALL user-scoped stores unconditionally. Used as a last-resort fallback
 * when the caller doesn't know which user's data to clear (e.g. a token was
 * dropped before we could read its sub claim).
 *
 * Prefer `clearCurrentUserData(userId)` whenever the user id is known — this
 * function is a blunt instrument and (because book_files / book_covers /
 * pending_changes have no user_id index) may touch data that belonged to
 * other users' sessions if they ever coexisted on this device.
 *
 * Shared content-addressed caches (canonical_books, chapter_chunks,
 * chapter_registry, chunk_explanations) are preserved — they are LLM outputs
 * keyed by content hash and expensive to recompute.
 */
export async function clearUserData() {
    if (!(await ensureDB())) return;
    const db = getDB();
    const allStores = [
        'pending_changes', 'sync_meta',
        'books', 'book_files', 'book_covers', 'highlights', 'notes', 'note_revisions',
        'bookmarks', 'progress',
        'user_chunk_profile', 'chunk_interactions',
    ];

    for (const storeName of allStores) {
        try {
            await new Promise((resolve, reject) => {
                const tx = db.transaction(storeName, 'readwrite');
                const store = tx.objectStore(storeName);
                const request = store.clear();

                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
            });
        } catch (e) {
            console.warn(`Failed to clear store ${storeName}:`, e);
        }
    }
}

/**
 * Clear all data belonging to a specific user. Used by logout and account
 * switch — preferred over `clearUserData()` because it is safe against
 * cross-user data pollution that would otherwise survive in blob stores.
 *
 * Three categories of data:
 *
 * 1. User-indexed stores (books / highlights / notes / bookmarks / progress /
 *    user_chunk_profile) — filter by user_id index, delete matching records.
 *
 * 2. Opaque blobs with no user_id index:
 *    - book_files: SELECTIVELY deleted. Only books with a valid cloud
 *      `file_url` (R2 upload) OR a curated-book URL can have their local
 *      blob safely removed — the binary can be re-downloaded on next
 *      login. Books WITHOUT file_url are "local-only" (user uploaded via
 *      Library drag-drop but never synced the file to R2); the local blob
 *      is the ONLY copy, so deleting it = permanent data loss. We preserve
 *      local-only book_files across logout. The book metadata is still
 *      removed (it will be re-fetched from cloud on next login, and the
 *      orphan blob will be re-linked when the user re-adds the book).
 *    - book_covers: same policy as book_files (safe to drop when file_url
 *      exists; covers can be re-extracted from blob or re-downloaded).
 *    - chunk_interactions: cleared via the user_book compound index cursor.
 *
 * 3. Per-session bookkeeping (pending_changes / sync_meta) — these belong
 *    entirely to the currently-logged-in user, so we clear() wholesale.
 *    Safe because the app enforces one-logged-in-account-at-a-time and
 *    always clears before switching.
 *
 * Shared content-addressed caches (canonical_books, chapter_chunks,
 * chapter_registry, chunk_explanations) are deliberately preserved — they
 * are LLM outputs keyed by content hash and expensive to recompute.
 *
 * NB: When adding a new user-scoped IndexedDB store, update this function.
 * The schema lives at src/lib/db/helpers.js.
 */
export async function clearCurrentUserData(userId) {
    if (!userId) {
        console.warn('[clearCurrentUserData] no userId — falling back to clearUserData');
        return clearUserData();
    }
    if (!(await ensureDB())) return;
    const db = getDB();

    // Step 1: snapshot the user's books BEFORE clearing the books store.
    // We need the full records (not just ids) so we can decide which blobs
    // are safe to delete based on each book's file_url.
    //
    // Rule: a book's local blob is safe to delete only when it has a
    // re-downloadable cloud source:
    //   - `e00396.../xxx.epub`      → R2 upload, safe
    //   - `curated-books/xxx`       → curated library, safe
    //   - `/reader/curated-books/…` → curated API path, safe
    //   - null / empty              → LOCAL-ONLY, blob is the only copy
    //
    // Books without file_url are NOT deleted from book_files / book_covers.
    // The orphan blob will be picked up automatically if the user re-adds
    // the same file (bookStore.saveBookFile uses book id as key).
    let cloudBackedBookIds = [];
    try {
        const userBooks = await getAllForUserRaw('books', userId);
        cloudBackedBookIds = userBooks
            .filter((b) => !!b.file_url) // has any cloud source (R2 upload or curated URL)
            .map((b) => b.id)
            .filter(Boolean);
    } catch (e) {
        console.warn('[clearCurrentUserData] failed to snapshot book ids:', e);
    }

    // Step 2: delete records from user-indexed stores.
    // note_revisions has a user_id index (helpers.js v13) so the same loop
    // works — keeps logout symmetry with notes (no orphan history left behind).
    const userIndexedStores = [
        'books', 'highlights', 'notes', 'note_revisions', 'bookmarks', 'progress', 'user_chunk_profile',
    ];
    for (const storeName of userIndexedStores) {
        try {
            const items = await getAllForUserRaw(storeName, userId);
            for (const item of items) {
                await remove(storeName, item.id);
            }
        } catch (e) {
            console.warn(`[clearCurrentUserData] failed to clear ${storeName}:`, e);
        }
    }

    // Step 3: delete opaque blobs ONLY for cloud-backed books.
    // Local-only books (file_url === null) keep their binary across logout;
    // it is the only copy of the user's data.
    for (const bookId of cloudBackedBookIds) {
        try { await deleteBookFile(bookId); } catch { /* best-effort */ }
        try { await deleteBookCover(bookId); } catch { /* best-effort */ }
    }

    // Step 4: clear chunk_interactions via the user_book compound index cursor.
    // Keys are [user_id, book_id]; we open a range from [userId, ''] to
    // [userId, '\uffff'] to cover all book_ids for this user.
    if (db.objectStoreNames.contains('chunk_interactions')) {
        try {
            await new Promise((resolve, reject) => {
                const tx = db.transaction('chunk_interactions', 'readwrite');
                const store = tx.objectStore('chunk_interactions');
                const index = store.index('user_book');
                const range = IDBKeyRange.bound([userId, ''], [userId, '\uffff']);
                const cursorReq = index.openCursor(range);
                cursorReq.onsuccess = (event) => {
                    const cursor = event.target.result;
                    if (cursor) {
                        cursor.delete();
                        cursor.continue();
                    }
                };
                tx.oncomplete = () => resolve();
                tx.onerror = () => reject(tx.error);
            });
        } catch (e) {
            console.warn('[clearCurrentUserData] failed to clear chunk_interactions:', e);
        }
    }

    // Step 5: wipe pending_changes and sync_meta wholesale — these are
    // per-session bookkeeping for whoever is logged in right now.
    for (const storeName of ['pending_changes', 'sync_meta']) {
        try {
            await new Promise((resolve, reject) => {
                const tx = db.transaction(storeName, 'readwrite');
                const store = tx.objectStore(storeName);
                const req = store.clear();
                req.onsuccess = () => resolve();
                req.onerror = () => reject(req.error);
            });
        } catch (e) {
            console.warn(`[clearCurrentUserData] failed to wipe ${storeName}:`, e);
        }
    }
}

/**
 * Get all locally-pending items (created/modified offline, not yet confirmed by server).
 * Used during full sync to preserve data the server may not have returned.
 */
export async function getPendingItems(storeName, userId) {
    const items = await getAllForUserRaw(storeName, userId);
    return items.filter(item => item._pending);
}

/** Get a single item by id from any store (used by sync merge logic). */
export async function getItem(storeName, id) {
    return getById(storeName, id);
}

/** Get all items for a user (including soft-deleted, raw). Used by sync merge. */
export async function getAllForUser(storeName, userId) {
    return getAllForUserRaw(storeName, userId);
}

/**
 * Clear all records in a store for a user (including soft-deleted).
 * Used during full sync so local state matches server exactly.
 */
export async function clearUserStoreForReplace(storeName, userId) {
    if (!(await ensureDB())) return;
    const items = await getAllForUserRaw(storeName, userId);
    for (const item of items) {
        await remove(storeName, item.id);
    }
}

/**
 * Remove books for this user whose id is not in keepIds (e.g. server ids + local file ids).
 * Used when applying full sync so local book list matches server (+ local-only with file).
 */
export async function removeBooksForUserExcept(userId, keepIds) {
    if (!(await ensureDB())) return;
    const userBooks = await getAll('books', userId);
    const keep = new Set(keepIds);
    for (const book of userBooks) {
        if (!keep.has(book.id)) {
            await remove('books', book.id);
        }
    }
}
